import {
  createDiscoveryCommitment,
  createKeyConfirmation,
  createSenderHandshake,
  completeSenderHandshake,
  derivePhysicalFrameAuthKey,
  hashHex,
  openPhysicalFrameEnvelope,
  sealObject,
  sealPhysicalFrameEnvelope,
  verifyKeyConfirmation,
  type SealedObject,
  type SenderHandshakeState,
  type SplitPeerSession,
} from '@spectrabus/crypto';
import { GenerationEncoder } from '@spectrabus/fountain';
import { encodeOpticalPayload, type OpticalFrame } from '@spectrabus/optical-encoder';
import {
  DEFAULT_CHUNK_SIZE,
  ProtocolError,
  SequenceReplayError,
  assertPhaseTransition,
  encodeTransportPacket,
  equalBytes,
  hexToBytes,
  randomIdentifier,
  type PhysicalChannel,
  type TransferPhase,
  type TransportPacket,
} from '@spectrabus/protocol';
import {
  decodeRankFeedback,
  encodeManifestSegment,
  peekPhysicalFrameHeader,
  segmentManifest,
  payloadMagic,
  type ManifestSegment,
  type RankFeedback,
} from './control-frames';
import {
  ACOUSTIC_FORWARD_PROFILE_ID,
  ACOUSTIC_RETURN_PROFILE_ID,
  OPTICAL_PROFILE_ID,
  ReplayWindows,
  SequenceAllocator,
  feedbackContextId,
  liveEvent,
  liveFountainSeed,
  manifestContextId,
  type LiveEvent,
  type LiveObjectInput,
} from './shared';

const SBHS_MAGIC = 0x53424853;
const KEY_CONFIRM_TIMEOUT_MS = 120_000;
const CONFIRMATION_INTERLEAVE_PERIOD = 6;
const MANIFEST_INTERLEAVE_PERIOD = 2;

interface SenderGeneration {
  readonly encoder: GenerationEncoder;
  nextEquationId: number;
  /** Latest deficit the receiver reported; sourceCount until first feedback. */
  reportedDeficit: number;
}

export interface SenderEmission {
  /** SBFA-sealed or raw SBHS bytes ready for the physical channel. */
  readonly payload: Uint8Array;
  /** The O1 frame sequence, meaningful for the optical channel only. */
  readonly sequence: number;
  readonly kind: 'offer' | 'confirmation' | 'manifest' | 'data';
}

export interface SenderLedger {
  opticalEmissions: number;
  acousticEmissions: number;
  opticalEmittedBytes: number;
  acousticEmittedBytes: number;
  returnPayloadsAccepted: number;
  returnPayloadsRejected: number;
  feedbackFrames: number;
}

/**
 * Drives a real one-way object transfer from protocol state. The engine is
 * transport-agnostic: the caller presents optical frames on a display, plays
 * acoustic payloads through a speaker and feeds every payload captured from
 * the return band into acceptReturnPayload. All cryptography stays in this
 * realm because split-peer sessions cannot cross a worker boundary.
 */
export class SenderLiveSession {
  #phase: TransferPhase = 'DISCOVER';
  #phaseDetail = 'Emitting the discovery offer and listening on the return band.';
  #failure: string | null = null;
  #handshake: SenderHandshakeState;
  #session: SplitPeerSession | null = null;
  #sealed: SealedObject | null = null;
  #frameAuthKey: Uint8Array | null = null;
  #responseBytes: Uint8Array | null = null;
  #generations: SenderGeneration[] = [];
  #manifestSegments: readonly ManifestSegment[] = [];
  #seed: string | null = null;
  #sequences = new SequenceAllocator();
  #returnReplayWindows = new ReplayWindows();
  #confirmationReplayWindows = new ReplayWindows();
  #verifiedReturnChannels = new Set<PhysicalChannel>();
  #receiverAuthorised = false;
  #receiverManifestComplete = false;
  #receiverForwardAudioDisabled = false;
  #lastReturnAcceptedAt: number | null = null;
  #latestFeedback: RankFeedback | null = null;
  #receiverObjectHash: string | null = null;
  #integrityViolations = 0;
  #emissionCounter = 0;
  #confirmationRotation = 0;
  #lastOpticalFrame: OpticalFrame;
  #phaseEnteredAt: number;
  #createdAt: number;
  readonly #input: LiveObjectInput;
  readonly #sessionId: string;
  readonly #transferId: string;
  readonly #discoveryCommitment: string;
  readonly #senderObjectHash: string;
  public readonly ledger: SenderLedger = {
    opticalEmissions: 0,
    acousticEmissions: 0,
    opticalEmittedBytes: 0,
    acousticEmittedBytes: 0,
    returnPayloadsAccepted: 0,
    returnPayloadsRejected: 0,
    feedbackFrames: 0,
  };

  private constructor(
    input: LiveObjectInput,
    sessionId: string,
    transferId: string,
    discoveryCommitment: string,
    senderObjectHash: string,
    handshake: SenderHandshakeState,
    nowMs: number,
  ) {
    this.#input = input;
    this.#sessionId = sessionId;
    this.#transferId = transferId;
    this.#discoveryCommitment = discoveryCommitment;
    this.#senderObjectHash = senderObjectHash;
    this.#handshake = handshake;
    this.#createdAt = nowMs;
    this.#phaseEnteredAt = nowMs;
    // Before authorisation only the public discovery offer may occupy the
    // raster, mirroring the reference choreography in the simulation.
    this.#lastOpticalFrame = encodeOpticalPayload(handshake.offerBytes, 0);
  }

  public static async create(input: LiveObjectInput, nowMs = Date.now()): Promise<SenderLiveSession> {
    if (input.bytes.length === 0) {
      throw new ProtocolError('LIVE_OBJECT_EMPTY', 'A live transfer requires at least one byte.');
    }
    const sessionId = randomIdentifier(16);
    const transferId = randomIdentifier(16);
    const senderObjectHash = await hashHex(input.bytes);
    const discoveryCommitment = await createDiscoveryCommitment({
      name: input.name,
      mimeType: input.mimeType,
      byteLength: input.bytes.length,
      objectHash: senderObjectHash,
      chunkSize: DEFAULT_CHUNK_SIZE,
      compression: 'none',
    });
    const handshake = await createSenderHandshake({
      sessionId,
      transferId,
      discoveryCommitment,
      offerSequence: 0,
      channels: ['optical', 'acoustic'],
    });
    return new SenderLiveSession(
      input,
      sessionId,
      transferId,
      discoveryCommitment,
      senderObjectHash,
      handshake,
      nowMs,
    );
  }

  public get phase(): TransferPhase {
    return this.#phase;
  }

  public get phaseDetail(): string {
    return this.#phaseDetail;
  }

  public get failure(): string | null {
    return this.#failure;
  }

  public get sessionId(): string {
    return this.#sessionId;
  }

  public get transferId(): string {
    return this.#transferId;
  }

  public get discoveryCommitment(): string {
    return this.#discoveryCommitment;
  }

  public get senderObjectHash(): string {
    return this.#senderObjectHash;
  }

  public get receiverObjectHash(): string | null {
    return this.#receiverObjectHash;
  }

  public get offerBytes(): Uint8Array {
    return this.#handshake.offerBytes.slice();
  }

  public get objectName(): string {
    return this.#input.name;
  }

  public get objectMimeType(): string {
    return this.#input.mimeType;
  }

  public get objectByteLength(): number {
    return this.#input.bytes.length;
  }

  public get session(): SplitPeerSession | null {
    return this.#session;
  }

  public get sealed(): SealedObject | null {
    return this.#sealed;
  }

  public get latestFeedback(): RankFeedback | null {
    return this.#latestFeedback;
  }

  public get integrityViolations(): number {
    return this.#integrityViolations;
  }

  public get verifiedReturnChannels(): readonly PhysicalChannel[] {
    return [...this.#verifiedReturnChannels];
  }

  public get receiverAuthorised(): boolean {
    return this.#receiverAuthorised;
  }

  public get lastOpticalFrame(): OpticalFrame {
    return this.#lastOpticalFrame;
  }

  public get generationSummaries(): readonly {
    generationId: number;
    sourceCount: number;
    validBytes: number;
    reportedDeficit: number;
  }[] {
    return this.#generations.map((generation, generationId) => ({
      generationId,
      sourceCount: generation.encoder.sourceCount,
      validBytes: generation.encoder.validBytes,
      reportedDeficit: generation.reportedDeficit,
    }));
  }

  public get elapsedMs(): number {
    return Date.now() - this.#createdAt;
  }

  #transition(next: TransferPhase, detail: string, nowMs: number): void {
    assertPhaseTransition(this.#phase, next);
    this.#phase = next;
    this.#phaseDetail = detail;
    this.#phaseEnteredAt = nowMs;
  }

  #fail(message: string, nowMs: number): void {
    if (this.#phase !== 'FAILED') {
      this.#phase = 'FAILED';
      this.#phaseEnteredAt = nowMs;
    }
    this.#failure = message;
    this.#phaseDetail = message;
  }

  /** Applies watchdogs; call at UI cadence with the current wall clock. */
  public tick(nowMs = Date.now()): void {
    if (this.#phase === 'KEY_CONFIRM' && nowMs - this.#phaseEnteredAt > KEY_CONFIRM_TIMEOUT_MS) {
      this.#fail(
        'Key confirmation window elapsed without mutual channel authorisation.',
        nowMs,
      );
      return;
    }
    if (this.#phase === 'TRANSFER' || this.#phase === 'REPAIR') {
      // Fountain emission is complete in itself, so a lost return path never
      // fails the transfer — but the operator must know the peers are no
      // longer in the same conversation instead of watching a silent loop.
      const lastHeard = this.#lastReturnAcceptedAt ?? this.#phaseEnteredAt;
      if (nowMs - lastHeard > 90_000) {
        this.#phaseDetail =
          'Emitting sealed transport frames, but no return feedback for over ninety seconds — the receiver may have stopped or lost its return path. Emission continues; use the wordmark to abandon the session.';
      }
    }
  }

  async #completeHandshake(responseBytes: Uint8Array, nowMs: number): Promise<LiveEvent[]> {
    const session = await completeSenderHandshake(this.#handshake, responseBytes);
    this.#session = session;
    this.#responseBytes = responseBytes.slice();
    // Sealing is keyed by the handshake: the object root key exists only
    // after the response, so a sender cannot emit sealed payload towards a
    // peer it has not keyed with.
    const sealed = await sealObject(
      this.#input.bytes,
      { name: this.#input.name, mimeType: this.#input.mimeType },
      DEFAULT_CHUNK_SIZE,
      session.objectRootKey,
    );
    const recomputedCommitment = await createDiscoveryCommitment({
      name: sealed.manifest.name,
      mimeType: sealed.manifest.mimeType,
      byteLength: sealed.manifest.byteLength,
      objectHash: sealed.manifest.objectHash,
      chunkSize: sealed.manifest.chunkSize,
      compression: sealed.manifest.compression,
    });
    if (recomputedCommitment !== this.#discoveryCommitment) {
      throw new ProtocolError(
        'LIVE_DISCOVERY_COMMITMENT',
        'Sealed manifest does not reproduce the committed discovery object.',
      );
    }
    this.#sealed = sealed;
    this.#frameAuthKey = await derivePhysicalFrameAuthKey(session.objectRootKey, this.#sessionId);
    this.#seed = liveFountainSeed(this.#sessionId, sealed.manifest.objectHash);
    this.#generations = sealed.chunks.map((chunk, generationId) => ({
      encoder: new GenerationEncoder(this.#seed ?? '', generationId, chunk.ciphertext),
      nextEquationId: 0,
      reportedDeficit: Number.POSITIVE_INFINITY,
    }));
    this.#manifestSegments = segmentManifest(sealed.manifestBytes);
    this.#transition('CALIBRATE', 'Receiver key response accepted; deterministic plans in force.', nowMs);
    this.#transition('KEY_CONFIRM', 'Exchanging per-channel key confirmations physically.', nowMs);
    return [
      liveEvent('response-accepted', null, 'Receiver key response bound to the offer transcript.'),
      liveEvent('phase-changed', null, 'KEY_CONFIRM'),
    ];
  }

  async #handleConfirmation(bytes: Uint8Array, nowMs: number): Promise<LiveEvent[]> {
    const session = this.#session;
    if (session === null) {
      this.ledger.returnPayloadsRejected += 1;
      return [liveEvent('rejected', null, 'Confirmation received before the key response.')];
    }
    let message;
    try {
      message = await verifyKeyConfirmation(session, bytes, {
        direction: 'receiver-to-sender',
        channel: this.#peekConfirmationChannel(bytes),
        replayWindow: this.#confirmationReplayWindows.for(
          'receiver-to-sender',
          this.#peekConfirmationChannel(bytes),
        ),
      });
    } catch (error) {
      if (error instanceof SequenceReplayError) {
        // A repeated burst of an already verified confirmation is expected
        // physical behaviour, not an attack signal.
        return [liveEvent('confirmation-duplicate', null, 'Repeated confirmation burst ignored.')];
      }
      this.ledger.returnPayloadsRejected += 1;
      this.#integrityViolations += 1;
      return [
        liveEvent(
          'rejected',
          null,
          error instanceof Error ? error.message : 'Key confirmation rejected.',
        ),
      ];
    }
    const alreadyVerified = this.#verifiedReturnChannels.has(message.channel);
    this.#verifiedReturnChannels.add(message.channel);
    const events: LiveEvent[] = alreadyVerified
      ? [liveEvent('confirmation-duplicate', message.channel, 'Channel already confirmed.')]
      : [liveEvent('confirmation-verified', message.channel, `Return direction confirmed on ${message.channel}.`)];
    if (
      this.#phase === 'KEY_CONFIRM' &&
      this.#verifiedReturnChannels.size === (this.#session?.channels.length ?? 2)
    ) {
      this.#transition(
        'TRANSFER',
        'Both return confirmations verified; emitting sealed transport frames.',
        nowMs,
      );
      events.push(liveEvent('phase-changed', null, 'TRANSFER'));
    }
    return events;
  }

  #peekConfirmationChannel(bytes: Uint8Array): PhysicalChannel {
    // The confirmation channel is a clear-text wire field at offset 8; the
    // authenticated verify recomputes and checks it. Malformed values fall
    // back to optical and fail verification there.
    return bytes[8] === 2 ? 'acoustic' : 'optical';
  }

  async #handleFeedback(envelope: Uint8Array, channel: PhysicalChannel, nowMs: number): Promise<LiveEvent[]> {
    const key = this.#frameAuthKey;
    if (key === null) {
      this.ledger.returnPayloadsRejected += 1;
      return [liveEvent('rejected', channel, 'Feedback frame before session keys exist.')];
    }
    const opened = await openPhysicalFrameEnvelope(envelope, key, {
      sessionId: this.#sessionId,
      direction: 'receiver-to-sender',
      channel,
      profileId: ACOUSTIC_RETURN_PROFILE_ID,
      contextId: feedbackContextId(this.#transferId),
      replayWindow: this.#returnReplayWindows.for('receiver-to-sender', channel),
    });
    const feedback = decodeRankFeedback(opened.payload);
    this.#latestFeedback = feedback;
    this.ledger.feedbackFrames += 1;
    this.#receiverAuthorised ||= feedback.transferAuthorised;
    this.#receiverManifestComplete ||= feedback.manifestReceived;
    this.#receiverForwardAudioDisabled ||= feedback.forwardAudioDisabled;
    for (let index = 0; index < feedback.rankDeficits.length; index += 1) {
      const generation = this.#generations[index];
      if (generation !== undefined) {
        generation.reportedDeficit = feedback.rankDeficits[index] ?? 0;
      }
    }
    const events: LiveEvent[] = [
      liveEvent('feedback-received', channel, `Rank feedback across ${feedback.rankDeficits.length} generations.`),
    ];
    if (feedback.objectVerified && feedback.receiverObjectHash !== null) {
      if (feedback.receiverObjectHash === this.#senderObjectHash) {
        this.#receiverObjectHash = feedback.receiverObjectHash;
        if (this.#phase === 'TRANSFER' || this.#phase === 'REPAIR') {
          this.#transition('VERIFY', 'Receiver reports full reconstruction; comparing hashes.', nowMs);
          this.#transition('COMPLETE', 'Receiver verified the object end to end.', nowMs);
          events.push(liveEvent('object-verified', null, 'Receiver hash matches the sender hash.'));
          events.push(liveEvent('phase-changed', null, 'COMPLETE'));
        }
      } else {
        this.#integrityViolations += 1;
        this.#fail('Receiver reported an object hash that does not match the sender.', nowMs);
      }
    }
    return events;
  }

  /**
   * Feeds one payload captured from the return acoustic band. Accepts the
   * 141-byte key response, 96-byte key confirmations and SBFA-sealed rank
   * feedback; everything else is counted and dropped.
   */
  public async acceptReturnPayload(
    bytes: Uint8Array,
    nowMs = Date.now(),
  ): Promise<LiveEvent[]> {
    if (this.#phase === 'FAILED' || this.#phase === 'COMPLETE') {
      return [];
    }
    try {
      const magic = payloadMagic(bytes);
      if (magic === SBHS_MAGIC) {
        const type = bytes[6];
        if (type === 2) {
          if (this.#responseBytes !== null) {
            // completeSenderHandshake is deliberately single-use; a repeated
            // response burst is normal, a different response is an anomaly.
            if (equalBytes(this.#responseBytes, bytes)) {
              return [liveEvent('confirmation-duplicate', null, 'Repeated key response ignored.')];
            }
            this.#integrityViolations += 1;
            this.ledger.returnPayloadsRejected += 1;
            return [liveEvent('rejected', null, 'Conflicting key response ignored.')];
          }
          const events = await this.#completeHandshake(bytes, nowMs);
          this.ledger.returnPayloadsAccepted += 1;
          return events;
        }
        if (type === 3) {
          const events = await this.#handleConfirmation(bytes, nowMs);
          if (events.some((event) => event.kind === 'confirmation-verified')) {
            this.ledger.returnPayloadsAccepted += 1;
          }
          return events;
        }
        this.ledger.returnPayloadsRejected += 1;
        return [liveEvent('rejected', null, `Unsupported SBHS type ${type ?? 'unknown'}.`)];
      }
      const peeked = peekPhysicalFrameHeader(bytes);
      if (peeked !== null && peeked.direction === 'receiver-to-sender') {
        const events = await this.#handleFeedback(bytes, peeked.channel, nowMs);
        this.ledger.returnPayloadsAccepted += 1;
        this.#lastReturnAcceptedAt = nowMs;
        return events;
      }
      this.ledger.returnPayloadsRejected += 1;
      return [liveEvent('rejected', null, 'Unrecognised return payload.')];
    } catch (error) {
      if (error instanceof SequenceReplayError) {
        return [liveEvent('confirmation-duplicate', null, 'Repeated return frame ignored.')];
      }
      this.ledger.returnPayloadsRejected += 1;
      this.#integrityViolations += 1;
      return [
        liveEvent('rejected', null, error instanceof Error ? error.message : 'Return payload rejected.'),
      ];
    }
  }

  #packetFromSymbol(
    generationId: number,
    channel: PhysicalChannel,
    sequence: number,
  ): TransportPacket {
    const sealed = this.#sealed;
    const generation = this.#generations[generationId];
    if (sealed === null || generation === undefined) {
      throw new ProtocolError('LIVE_EMISSION_STATE', 'Transport emission requested before sealing.');
    }
    const symbol = generation.encoder.createSymbol(generation.nextEquationId);
    generation.nextEquationId += 1;
    return {
      sessionTag: hexToBytes(this.#sessionId).slice(0, 8),
      objectTag: hexToBytes(sealed.objectId).slice(0, 8),
      direction: 'sender-to-receiver',
      channel,
      sequence,
      generationId: symbol.generationId,
      equationId: symbol.equationId,
      sourceCount: symbol.sourceCount,
      symbolSize: symbol.symbolSize,
      validGenerationBytes: symbol.validBytes,
      payload: symbol.payload,
    };
  }

  #chooseGeneration(): number | null {
    if (this.#generations.length === 0) return null;
    // Directed repair once feedback exists: serve the largest reported
    // deficit first. Before any feedback, round-robin over emission count.
    let bestIndex: number | null = null;
    let bestDeficit = 0;
    for (let index = 0; index < this.#generations.length; index += 1) {
      const deficit = this.#generations[index]?.reportedDeficit ?? 0;
      if (deficit > bestDeficit) {
        bestDeficit = deficit;
        bestIndex = index;
      }
    }
    if (bestIndex !== null) return bestIndex;
    // Every reported deficit is zero: the receiver holds full rank and the
    // remaining distance is verification, not emission.
    return null;
  }

  #nextManifestSegment(): ManifestSegment | null {
    if (this.#manifestSegments.length === 0 || this.#receiverManifestComplete) return null;
    const index = Math.floor(this.#emissionCounter / MANIFEST_INTERLEAVE_PERIOD) % this.#manifestSegments.length;
    return this.#manifestSegments[index] ?? null;
  }

  async #emitConfirmation(channel: PhysicalChannel): Promise<Uint8Array> {
    const session = this.#session;
    if (session === null) {
      throw new ProtocolError('LIVE_EMISSION_STATE', 'Confirmation emission before key response.');
    }
    return createKeyConfirmation(
      session,
      'sender-to-receiver',
      channel,
      this.#sequences.next('sender-to-receiver', channel),
    );
  }

  #confirmationChannelFor(transport: PhysicalChannel): PhysicalChannel {
    if (transport === 'acoustic') return 'acoustic';
    // The optical path alternates both channels' confirmations, so a
    // receiver that cannot hear the forward audio band (a speaker-priority
    // phone) still authorises every negotiated channel. Verification binds
    // to the message's own channel field, never to the physical path that
    // happened to carry it.
    this.#confirmationRotation += 1;
    return this.#confirmationRotation % 2 === 0 ? 'optical' : 'acoustic';
  }

  /**
   * Produces the next payload for one forward channel, or null when the
   * channel should stay quiet. The caller owns cadence; every call advances
   * the emission schedule by exactly one frame.
   */
  public async nextForwardPayload(channel: PhysicalChannel): Promise<SenderEmission | null> {
    if (this.#phase === 'FAILED') return null;
    // Until the transfer is authorised the sender is also a listener: its
    // microphone waits for the faint return-band response while its own
    // speaker sits centimetres away. The preamble correlator normalises
    // against total window power, so even out-of-band self-emission raises
    // the acquisition floor. The screen therefore carries the offer and the
    // confirmations alone, and the speaker stays silent until TRANSFER.
    if (channel === 'acoustic' && this.#phase !== 'TRANSFER' && this.#phase !== 'REPAIR') {
      return null;
    }
    // A receiver that declared its forward-audio reception disabled hears
    // nothing on this channel; emitting would only add room noise and
    // near-end interference against its own return band.
    if (channel === 'acoustic' && this.#receiverForwardAudioDisabled) {
      return null;
    }
    if (this.#phase === 'DISCOVER') {
      // Only the public offer may be emitted before authorisation, and it
      // always rides frame sequence zero like the reference choreography.
      return { payload: this.#handshake.offerBytes.slice(), sequence: 0, kind: 'offer' };
    }
    const sealed = this.#sealed;
    const key = this.#frameAuthKey;
    if (sealed === null || key === null || this.#session === null) return null;

    this.#emissionCounter += 1;

    if (this.#phase === 'KEY_CONFIRM') {
      const payload = await this.#emitConfirmation(this.#confirmationChannelFor(channel));
      return { payload, sequence: this.#sequences.next('sender-to-receiver', channel), kind: 'confirmation' };
    }

    if (this.#phase !== 'TRANSFER' && this.#phase !== 'REPAIR') {
      // COMPLETE keeps the channels quiet; the receiver has everything.
      return null;
    }

    // Interleave confirmations into the data stream until the receiver
    // signals authorisation, so a missed burst cannot deadlock admission.
    if (!this.#receiverAuthorised && this.#emissionCounter % CONFIRMATION_INTERLEAVE_PERIOD === 0) {
      const payload = await this.#emitConfirmation(this.#confirmationChannelFor(channel));
      return { payload, sequence: this.#sequences.next('sender-to-receiver', channel), kind: 'confirmation' };
    }

    const profileId = channel === 'optical' ? OPTICAL_PROFILE_ID : ACOUSTIC_FORWARD_PROFILE_ID;
    const manifestSegment = this.#nextManifestSegment();
    if (manifestSegment !== null && this.#emissionCounter % MANIFEST_INTERLEAVE_PERIOD === 0) {
      const sequence = this.#sequences.next('sender-to-receiver', channel);
      const payload = await sealPhysicalFrameEnvelope(encodeManifestSegment(manifestSegment), key, {
        sessionId: this.#sessionId,
        direction: 'sender-to-receiver',
        channel,
        sequence,
        symbolId: manifestSegment.segmentIndex,
        profileId,
        contextId: manifestContextId(this.#transferId),
      });
      return { payload, sequence, kind: 'manifest' };
    }

    const generationId = this.#chooseGeneration();
    if (generationId === null) {
      // Nothing left to emit for rank; keep repeating the manifest so a
      // receiver that lost it can still finish, otherwise stay quiet.
      if (manifestSegment !== null) {
        const sequence = this.#sequences.next('sender-to-receiver', channel);
        const payload = await sealPhysicalFrameEnvelope(encodeManifestSegment(manifestSegment), key, {
          sessionId: this.#sessionId,
          direction: 'sender-to-receiver',
          channel,
          sequence,
          symbolId: manifestSegment.segmentIndex,
          profileId,
          contextId: manifestContextId(this.#transferId),
        });
        return { payload, sequence, kind: 'manifest' };
      }
      return null;
    }
    const sequence = this.#sequences.next('sender-to-receiver', channel);
    const packet = this.#packetFromSymbol(generationId, channel, sequence);
    const payload = await sealPhysicalFrameEnvelope(encodeTransportPacket(packet), key, {
      sessionId: this.#sessionId,
      direction: packet.direction,
      channel,
      sequence,
      symbolId: packet.equationId,
      profileId,
      contextId: sealed.objectId,
    });
    return { payload, sequence, kind: 'data' };
  }

  /** Emits the next optical frame and records it as the presented raster. */
  public async nextOpticalFrame(): Promise<OpticalFrame | null> {
    const emission = await this.nextForwardPayload('optical');
    if (emission === null) return null;
    const frame = encodeOpticalPayload(emission.payload, emission.sequence);
    this.#lastOpticalFrame = frame;
    this.ledger.opticalEmissions += 1;
    this.ledger.opticalEmittedBytes += emission.payload.length;
    return frame;
  }

  /** Emits the next acoustic forward payload for the modem to synthesise. */
  public async nextAcousticPayload(): Promise<Uint8Array | null> {
    const emission = await this.nextForwardPayload('acoustic');
    if (emission === null) return null;
    this.ledger.acousticEmissions += 1;
    this.ledger.acousticEmittedBytes += emission.payload.length;
    return emission.payload;
  }
}
